import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, IconButton, styled, Typography } from "@mui/material";
import { MeasurementValues } from "@novorender/api";
import { spacing } from "@procore/core-react";
import { useEffect, useState } from "react";

import { useAppDispatch } from "app/redux-store-interactions";
import { DrawerProps } from "contexts/drawers/reducer";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { MeasuredResult } from "features/measure/measuredObjectProcore";
import { measureActions } from "features/measure/measureSlice";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { type ExtendedMeasureEntity } from "types/misc";
import { getMeasurementValueKind } from "utils/misc";

import { useDimensionData } from "./useDimensionData";

const PanelContent = styled(Box)`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const ContentSection = styled(Box)`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 16px;
`;

function useEntityValues(entity: ExtendedMeasureEntity | undefined) {
    const {
        state: { view },
    } = useExplorerGlobals();
    const [values, setValues] = useState<MeasurementValues>();

    useEffect(() => {
        if (!entity || !view) {
            setValues(undefined);
            return;
        }
        let cancelled = false;
        view.measure?.core.measure(entity, undefined, entity.settings).then((result) => {
            if (!cancelled && result) {
                setValues(result);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [entity, view]);

    return values;
}

function EntityCard({ entity }: { entity: ExtendedMeasureEntity }) {
    const values = useEntityValues(entity);
    const uom = useUom();

    if (!values || !("kind" in values)) {
        return (
            <Box p={1}>
                <Typography color="text.secondary" variant="body2">
                    Loading entity data...
                </Typography>
            </Box>
        );
    }

    const kind = getMeasurementValueKind(values);
    const rows: { label: string; value: string }[] = [];

    switch (values.kind) {
        case "line":
            rows.push({
                label: "Length",
                value: convertAndFormat(uom, values.distance.valueOf(), UnitType.length, { decimals: 3 }),
            });
            break;
        case "arc":
            rows.push({
                label: "Diameter",
                value: convertAndFormat(uom, values.radius.valueOf() * 2, UnitType.length, { decimals: 3 }),
            });
            rows.push({
                label: "Angle",
                value: convertAndFormat(uom, Math.round(values.totalAngle * (180 / Math.PI)), UnitType.angle),
            });
            break;
        case "plane":
            if (values.height) {
                rows.push({
                    label: "Height",
                    value: convertAndFormat(uom, values.height, UnitType.length, { decimals: 3 }),
                });
            }
            if (values.width) {
                rows.push({
                    label: "Width",
                    value: convertAndFormat(uom, values.width, UnitType.length, { decimals: 3 }),
                });
            }
            if (values.area) {
                rows.push({
                    label: "Area",
                    value: convertAndFormat(uom, values.area.valueOf(), UnitType.area, { decimals: 3 }),
                });
            }
            if (values.innerRadius) {
                rows.push({
                    label: "Diameter",
                    value: convertAndFormat(uom, values.innerRadius * 2, UnitType.length, { decimals: 3 }),
                });
            }
            break;
        case "cylinder":
            rows.push({
                label: "Diameter",
                value: convertAndFormat(uom, values.radius * 2, UnitType.length, { decimals: 3 }),
            });
            break;
        case "lineStrip":
            if (values.totalLength) {
                rows.push({
                    label: "Length",
                    value: convertAndFormat(uom, values.totalLength, UnitType.length, { decimals: 3 }),
                });
            }
            break;
        case "polymesh":
            if (values.area) {
                rows.push({
                    label: "Area",
                    value: convertAndFormat(uom, values.area.valueOf(), UnitType.area, { decimals: 3 }),
                });
            }
            break;
    }

    return (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "grey.50", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, textTransform: "capitalize" }}>
                {kind || "Entity"}
            </Typography>
            {rows.length > 0 ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${spacing.sm}px` }}>
                    {rows.map((row, i) => (
                        <Box key={i}>
                            <Typography variant="caption" color="text.secondary">
                                {row.label}
                            </Typography>
                            <Typography variant="body2">{row.value}</Typography>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No parametric data
                </Typography>
            )}
        </Box>
    );
}

export function DimensionDrawerPanel({ onClose: _ }: DrawerProps) {
    const dispatch = useAppDispatch();
    const { data, loading, hasData } = useDimensionData();

    return (
        <PanelContent>
            {data && data.totalSets > 1 && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} py={1} flexShrink={0}>
                    <IconButton
                        size="small"
                        onClick={() => dispatch(measureActions.selectMeasureSet(data.currentIndex - 1))}
                        disabled={data.currentIndex === 0}
                    >
                        <ChevronLeft fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ minWidth: "60px", textAlign: "center" }}>
                        {data.currentIndex + 1} / {data.totalSets}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => dispatch(measureActions.selectMeasureSet(data.currentIndex + 1))}
                        disabled={data.currentIndex === data.totalSets - 1}
                    >
                        <ChevronRight fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <ContentSection>
                {loading ? (
                    <Typography color="text.secondary" textAlign="center">
                        Loading...
                    </Typography>
                ) : !hasData || !data ? (
                    <Typography color="text.secondary" textAlign="center">
                        Select a surface to view dimensions
                    </Typography>
                ) : (
                    <>
                        {data.entities.map((obj, idx) => (
                            <EntityCard entity={obj as ExtendedMeasureEntity} key={idx} />
                        ))}
                        {data.result && <MeasuredResult duoMeasurementValues={data.result} />}
                    </>
                )}
            </ContentSection>
        </PanelContent>
    );
}
